// React Hooks:
import React, { useEffect, useState } from "react";
// React-Multi-Date-Picker Framework:
import { Calendar, CalendarProps, DateObject } from "react-multi-date-picker";
// import TimePicker from "react-multi-date-picker/plugins/time_picker";
// import TimePicker from "react-multi-date-picker/plugins/analog_time_picker";
import weekends from "react-multi-date-picker/plugins/highlight_weekends";
import Settings from "react-multi-date-picker/plugins/settings";
import Toolbar from "react-multi-date-picker/plugins/toolbar";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
// Jalali-Moment Framework:
import moment from "jalali-moment";
// MUI Components:
import Popover from "@mui/material/Popover";
import { Box } from "@mui/system";
// Styles:
import "./myCalendar.css";
import { Button } from "@mui/material";

// Type Controller
interface MyClalendarProps {
  vertical: number | "top" | "center" | "bottom";
  id: "popover" | undefined;
  open: boolean;
  popoverState: {
    anchorEl: HTMLButtonElement | null;
    popId: number;
  };
  handleClose: () => void;
  setFilterData: React.Dispatch<React.SetStateAction<any>>;
  fromDate?: string;
  toDate?: string;
}

const fa2enDigits = (s: string) =>
  s.replace(/[۰-۹]/g, (w) => String.fromCharCode(w.charCodeAt(0) - 1728));

const fa2enDate = (e: any) =>
  moment
    .from(fa2enDigits(e), "fa", "YYYY/MM/DD - HH:mm:ss")
    .format("YYYY/MM/DD - HH:mm:ss");

const en2faDate = (e: any) =>
  moment(fa2enDigits(e), "YYYY/MM/DD - HH:mm:ss")
    .locale("fa")
    .format("jYYYY/jMM/jDD - HH:mm:ss");

const dateNumberFN = (e: string) => {
  const dateArrey = e.split("/");

  let dateNumber = "";
  for (let i = 0; i < dateArrey.length; i++) {
    dateNumber += dateArrey[i];
  }

  return parseInt(dateNumber);
};

const MyCalendar: React.FC<MyClalendarProps> = ({
  vertical,
  id,
  open,
  popoverState,
  handleClose,
  setFilterData,
  fromDate,
  toDate,
}) => {
  const initialProps = {
    // value: new DateObject({ calendar: persian }),
    calendar: gregorian,
    locale: gregorian_en,
    // format: "YYYY/MM/DD - HH:mm:ss",
    format: "YYYY/MM/DD",
  };

  const [calendarProps, setCalendarProps] =
    useState<CalendarProps>(initialProps);
  const [dateLimit, setDateLimit] = useState({
    min: "",
    max: "",
  });
  const [toolbarDisabled, setToolbarDisabled] = useState<boolean>(false);
  const [calendarHeight, setCalendarHeight] = useState<string>("fit-content");
  
  const dateHandler = (date: any) => {
    switch (popoverState.popId) {
      case 1:
        setDateLimit((last) => ({ ...last, min: fa2enDigits(date.format()) }));
        // setFilterData((last: any) => ({
        //   ...last,
        //   fromDate: date.format(),
        // }));
        break;

      case 2:
        setDateLimit((last) => ({ ...last, max: fa2enDigits(date.format()) }));
        // setFilterData((last: any) => ({
        //   ...last,
        //   toDate: date.format(),
        // }));
        break;

      default:
        break;
    }
  };

  const propsHandler = (e: any) => {
    setCalendarProps(e);

    if (e.calendar.name !== calendarProps.calendar?.name) {
      setFilterData((last: any) => ({
        ...last,
        fromDate:
          !!last.fromDate && e.calendar.name === "persian"
            ? en2faDate(last.fromDate)
            : !!last.fromDate && e.calendar.name === "gregorian"
            ? fa2enDate(last.fromDate)
            : "",
        toDate:
          !!last.toDate && e.calendar.name === "persian"
            ? en2faDate(last.toDate)
            : !!last.toDate && e.calendar.name === "gregorian"
            ? fa2enDate(last.toDate)
            : "",
      }));

      setDateLimit((last) => ({
        ...last,
        min:
          !!last.min && calendarProps.calendar?.name === "persian"
            ? fa2enDate(last.min)
            : !!last.min && calendarProps.calendar?.name === "gregorian"
            ? en2faDate(last.min)
            : "",
        max:
          !!last.max && calendarProps.calendar?.name === "persian"
            ? fa2enDate(last.max)
            : !!last.max && calendarProps.calendar?.name === "gregorian"
            ? en2faDate(last.max)
            : "",
      }));
    }
  };

  const cleareHandler = () => {
    switch (popoverState.popId) {
      case 1:
        setDateLimit((last) => ({ ...last, min: "" }));
        setFilterData((last: any) => ({
          ...last,
          fromDate: "",
        }));
        setCalendarProps((last) => ({
          ...last,
          value: null,
        }));
        break;

      case 2:
        setDateLimit((last) => ({ ...last, max: "" }));
        setFilterData((last: any) => ({
          ...last,
          toDate: "",
        }));
        setCalendarProps((last) => ({
          ...last,
          value: null,
        }));
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    switch (popoverState.popId) {
      case 1:
        setCalendarProps((last: any) => ({
          ...last,
          value:
            dateLimit.min === ""
              ? null
              : new DateObject({
                  calendar: calendarProps.calendar,
                  date: dateLimit.min,
                  format: calendarProps.format,
                  locale: calendarProps.locale,
                }),
        }));
        break;

      case 2:
        setCalendarProps((last: any) => ({
          ...last,
          value:
            dateLimit.max === ""
              ? null
              : new DateObject({
                  calendar: calendarProps.calendar,
                  date: dateLimit.max,
                  format: calendarProps.format,
                  locale: calendarProps.locale,
                }),
        }));
        break;

      default:
        break;
    }
  }, [
    popoverState.popId,
    dateLimit.min,
    dateLimit.max,
    calendarProps.format,
    calendarProps.locale,
    calendarProps.calendar,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    if (
      (popoverState.popId === 1 &&
        !!dateLimit.max &&
        dateNumberFN(dateLimit.max) <
          dateNumberFN(
            new DateObject({
              calendar: calendarProps.calendar,
            }).format()
          )) ||
      (popoverState.popId === 2 &&
        !!dateLimit.min &&
        dateNumberFN(dateLimit.min) >
          dateNumberFN(
            new DateObject({
              calendar: calendarProps.calendar,
            }).format()
          ))
    ) {
      setToolbarDisabled(true);
    } else {
      setToolbarDisabled(false);
    }
  }, [
    calendarProps.calendar,
    dateLimit.max,
    dateLimit.min,
    popoverState.popId,
  ]);

  useEffect(() => {
    switch (popoverState.popId) {
      case 1:
        if (toolbarDisabled) {
          if (dateLimit.min === "") {
            setCalendarHeight("312px");
          } else {
            setCalendarHeight("352px");
          }
        } else {
          if (dateLimit.min === "") {
            setCalendarHeight("352px");
          } else {
            setCalendarHeight("387px");
          }
        }
        break;

      case 2:
        if (toolbarDisabled) {
          if (dateLimit.max === "") {
            setCalendarHeight("312px");
          } else {
            setCalendarHeight("352px");
          }
        } else {
          if (dateLimit.max === "") {
            setCalendarHeight("352px");
          } else {
            setCalendarHeight("387px");
          }
        }
        break;

      default:
        break;
    }
  }, [dateLimit.max, dateLimit.min, popoverState.popId, toolbarDisabled]);

  return (
    <>
      {(popoverState.popId === 1 || popoverState.popId === 2) && (
        <Popover
          id={id}
          open={open}
          anchorEl={popoverState.anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: vertical,
            horizontal: "center",
          }}
          sx={{
            mt: "8px",
          }}
        >
          <Box
            sx={{
              width: "255px",
              height: calendarHeight,
              overflow: "hidden",
              backgroundColor: "#fff",
              transition: "height 0.2s",
            }}
          >
            <Calendar
              {...calendarProps}
              onChange={dateHandler}
              onPropsChange={propsHandler}
              minDate={
                !!dateLimit.min && popoverState.popId === 2 ? dateLimit.min : ""
              }
              maxDate={
                !!dateLimit.max && popoverState.popId === 1 ? dateLimit.max : ""
              }
              plugins={[
                weekends(),
                <Settings
                  position="top"
                  calendars={["gregorian"]}
                  locales={["en"]}
                  disabledList={["other", "mode"]}
                  titles={
                    calendarProps.locale?.name === "persian_fa" ||
                    calendarProps.locale?.name === "gregorian_fa"
                      ? {
                          calendar: "تقویم",
                          locale: "زبان",
                          mode: "حالت",
                          otherPickers: "انتخاب کنندگان دیگر",
                          gregorian: "میلادی",
                          persian: "هجری شمسی",
                          arabic: "عربی",
                          indian: "هندی",
                          en: "انگلیسی",
                          fa: "فارسی",
                          ar: "عربی",
                          hi: "هندی",
                          single: "تکی",
                          multiple: "چندگانه",
                          range: "دامنه",
                          disable: "غیر فعال کردن",
                          onlyMonthPicker: "فقط انتخاب کننده ماه",
                          onlyYearPicker: "فقط انتخاب کننده سال",
                        }
                      : {
                          calendar: "Calendar",
                          locale: "Locale",
                          mode: "Mode",
                          otherPickers: "Other Pickers",
                          gregorian: "Gregorian",
                          persian: "Persian",
                          arabic: "Arabic",
                          indian: "Indian",
                          en: "English",
                          fa: "Farsi",
                          ar: "Arabic",
                          hi: "Hindi",
                          single: "Single",
                          multiple: "Multiple",
                          range: "Range",
                          disable: "Disable",
                          onlyMonthPicker: "Only Month Picker",
                          onlyYearPicker: "Only Year Picker",
                        }
                  }
                />,
                // <TimePicker position="right" />,
                <Toolbar
                  position="bottom"
                  sort={["today"]}
                  disabled={toolbarDisabled}
                />,
              ]}
            />
            <Box
              sx={{
                width: "100%",
                maxHeight: "100%",
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "row",
                justifyContent: calendarProps.value ? "space-evenly" : "center",
                backgroundColor: "#fff",
                borderRadius: "0px 0px 5px 5px",
                borderTop: toolbarDisabled ? "1px solid #cfd8e2" : "",
              }}
            >
              <Button
                sx={{
                  height: "28px",
                  minWidth: "0px",
                  backgroundColor: "#1c212d",
                  flexBasis: calendarProps.value ? "calc(48% - 3px)" : "0%",
                  borderRadius: "3px",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "12px",
                  lineHeight: "normal",
                  margin: toolbarDisabled
                    ? "6px 0px 6px 0px"
                    : "0px 0px 6px 0px",
                  padding: "3px 0",
                  textTransform: "none",
                  transition: "all 0.4s",
                  "&:hover": {
                    backgroundColor: "#1c212d",
                    boxShadow: "0 0 5px #8798ad",
                    transition: "0.4s",
                  },
                }}
                onClick={cleareHandler}
              >
                {calendarProps.locale?.name.split("_")[1] === "fa"
                  ? "پاک کردن"
                  : "Clear"}
              </Button>
              <Button
                sx={{
                  height: "28px",
                  minWidth: calendarProps.value ? "64px" : "0px",
                  backgroundColor: "#1c212d",
                  flexBasis: calendarProps.value ? "calc(48% - 3px)" : "96%",
                  borderRadius: "3px",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "12px",
                  lineHeight: "normal",
                  margin: toolbarDisabled
                    ? "6px 0px 6px 0px"
                    : "0px 0px 6px 0px",
                  padding: "3px 0",
                  textTransform: "none",
                  transition: "all 0.4s",
                  "&:hover": {
                    backgroundColor: "#1c212d",
                    boxShadow: "0 0 5px #8798ad",
                    transition: "0.4s",
                  },
                }}
                onClick={() => {
                  switch (popoverState.popId) {
                    case 1:
                      setFilterData((last: any) => ({
                        ...last,
                        fromDate: dateLimit.min,
                      }));
                      break;

                    case 2:
                      setFilterData((last: any) => ({
                        ...last,
                        toDate: dateLimit.max,
                      }));
                      break;
                    default:
                      break;
                  }
                  handleClose();
                }}
              >
                {calendarProps.locale?.name.split("_")[1] === "fa"
                  ? "ثبت"
                  : "Save"}
              </Button>
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default MyCalendar;
